/**
 * Checkout Schemas
 *
 * Zod validation schemas for checkout operations.
 * These validate data against the interfaces defined in @order-up/models.
 */

import { z } from 'zod';

const attributionField = z.string().min(1).max(256);

/** Same keys and caps as `decodeAttributionFragment` in @order-up/common. */
export const AdAttributionSchema = z
    .object({
        gaClientId: attributionField.optional(),
        gaSessionId: attributionField.optional(),
        fbp: attributionField.optional(),
        fbc: attributionField.optional(),
        landingUrl: attributionField.optional(),
        marketingAllowed: z.boolean(),
    })
    .strict();

/**
 * Customer information for checkout
 */
export const CheckoutCustomerInfoSchema = z.object({
    firstName: z.string().min(1, 'First name is required'),
    lastName: z.string().min(1, 'Last name is required'),
    // Required — the receipt channel that needs no opt-in (see the API's
    // POST /checkout body schema and apps/checkout/src/lib/customerContact.ts).
    email: z.string().email('Valid email is required'),
    phone: z.string().min(10, 'Valid phone number is required'),
});

/**
 * Billing address for payment processing
 */
export const CheckoutBillingAddressSchema = z.object({
    address: z.string().min(1, 'Street address is required'),
    address2: z.string().optional(),
    city: z.string().min(1, 'City is required'),
    region: z.string().min(2, 'State/region is required'),
    postal: z.string().min(5, 'Postal code is required'),
    country: z.string().length(2, 'Country code must be 2 characters').default('US'),
});

/**
 * TRX JS-library encrypted payload — the keyed-card instrument when the
 * restaurant routes to TRX. Mirrors `ICheckoutEncryptedPayment`
 * from `@order-up/models`.
 */
export const CheckoutEncryptedPaymentSchema = z.object({
    data: z.string().min(1, 'Encrypted data is required'),
    keyId: z.string().min(1, 'KeyId is required'),
    bin: z.string().optional(),
    lastFour: z.string().optional(),
    brand: z.string().optional(),
});

// TRX Apple Pay / Google Pay wallet payload (the JS library's
// onPaymentResult result.data + the cardSecurity cryptogram split).
export const CheckoutWalletPaymentSchema = z.object({
    walletName: z.enum(['ApplePay', 'GooglePay']),
    data: z.string().min(1, 'Wallet data is required'),
    keyId: z.string().optional(), // TRX JS-library path only
    bin: z.string().optional(),
    cardSecurity: z
        .object({
            cavv: z.string().optional(),
            dsrpCryptogram: z.string().optional(),
        })
        .optional(),
});

/**
 * Server-side checkout request
 * Contains all information needed to process a complete checkout
 *
 * Cross-field validation: exactly one of `encryptedPayment`,
 * `walletPayment`, or `selectedStorageSafeGuid` must be present (unless a
 * gift-card hold covers the order). Multiple
 * present means ambiguous dispatch — server cannot infer which
 * processor to send to.
 */
export const CheckoutRequestSchema = z.object({
    // Cart identification
    cartId: z.number().int().positive('Cart ID is required'),

    // Customer information
    customerInfo: CheckoutCustomerInfoSchema,

    // Payment information — exactly one required (validated below)
    encryptedPayment: CheckoutEncryptedPaymentSchema.optional(), // TRX JS library payload
    walletPayment: CheckoutWalletPaymentSchema.optional(), // TRX Apple Pay / Google Pay
    billingAddress: CheckoutBillingAddressSchema,

    // Optional payment features
    savePayment: z.boolean().optional().default(false),
    savedCardCvv: z.string().optional(), // For using saved cards
    selectedStorageSafeGuid: z.string().optional(), // Reuse saved card — TRX StorageSafe.Guid
    selectedAccountGuid: z.string().optional(), // TRX nested Account.Guid (optional)

    // Order configuration
    tipAmount: z.number().min(0).default(0),
    customerNotes: z.string().optional(),

    // Price validation (prevents charging different amount than user saw)
    expectedTotal: z.number().min(0).optional(),
    expectedDeliveryFee: z.number().min(0).optional(),

    // Transactional SMS opt-in (TCPA) — text the order confirmation + tracking
    // link only when the customer explicitly consented at checkout.
    smsConsent: z.boolean().optional().default(false),

    // PROMOTIONAL SMS opt-in — deliberately SEPARATE from `smsConsent` above.
    // Carriers and the TCPA treat marketing as a distinct consent requiring its
    // own affirmative, unbundled opt-in; agreeing to order updates is not
    // agreement to receive offers.
    //
    // No `.default()`: the tri-state matters. `undefined` means "the box was
    // not part of this request" and leaves any existing opt-in (e.g. captured
    // at the POS) untouched, whereas defaulting to `false` would let an
    // unrelated online order silently revoke it.
    promoText: z.boolean().optional(),

    // Coupon / promo-code REDEMPTION INTENT — the CODE, re-sent from preview. The
    // server re-derives + re-validates the discount (never a client amount).
    discountCode: z
        .string()
        .regex(/^[A-Za-z0-9]{3,20}$/, 'Promo code must be 3-20 alphanumeric characters')
        .optional(),

    // Gift card tender (spec 2026-10-08 §4.2) — a HOLD id, never a number.
    giftCardHoldId: z.string().min(1).max(300).optional(),
    expectedGiftCents: z.number().int().min(0).optional(),

    // Ad attribution from the origin page (Task 3, online-order measurement).
    // Strict as a schema. NOTE: the API route does not use CheckoutRequestSchema;
    // it declares a permissive Fastify schema and applies AdAttributionSchema in
    // the handler (invalid attribution is dropped, never a 400).
    attribution: AdAttributionSchema.optional(),

    // Idempotency — the attempt's key rides in this field, the Idempotency-Key
    // header, or both carrying the SAME value (the API resolves one key from
    // them and refuses two different ones). So the body field may be absent,
    // never empty.
    idempotencyKey: z.string().min(1, 'Idempotency key must not be empty').optional(),
}).superRefine((data, ctx) => {
    // Cross-field XOR: exactly one payment-method discriminator must
    // be present. Any combination (or none) is rejected so server-side
    // dispatch is unambiguous.
    const present = [
        data.encryptedPayment ? 'encryptedPayment' : null,
        data.walletPayment ? 'walletPayment' : null,
        data.selectedStorageSafeGuid ? 'selectedStorageSafeGuid' : null,
    ].filter((v): v is string => v !== null);
    // A gift card hold may cover the whole order; whether it does is the
    // server's call (it re-derives the total), so none is allowed WITH one.
    if (present.length === 0 && !data.giftCardHoldId) {
        ctx.addIssue({
            code: 'custom',
            message:
                'Exactly one of encryptedPayment, walletPayment, or selectedStorageSafeGuid is required',
            path: ['encryptedPayment'],
        });
    } else if (present.length > 1) {
        ctx.addIssue({
            code: 'custom',
            message: `Ambiguous payment method — multiple discriminators present: ${present.join(', ')}. Provide exactly one.`,
            path: [present[1]!],
        });
    }
});

/**
 * Server-side checkout response
 * Returned after successful payment processing
 */
export const CheckoutResponseSchema = z.object({
    success: z.boolean(),

    // Order information
    orderGuid: z.string().uuid(),
    orderId: z.number().int().positive(),
    orderNumber: z.number().int().positive(),

    // Payment information
    transactionRef: z.string(), // processor transaction reference (TRX Guid)
    amount: z.string(), // Formatted amount (e.g., "46.14")

    // Payment method (if saved)
    profileId: z.string().optional(),
    accountId: z.string().optional(),

    // Timestamps
    createdAt: z.string(), // ISO timestamp

    // Durable order-access token for the confirmation / receipt link.
    accessToken: z.string().optional(),
});

/**
 * Checkout error response
 */
export const CheckoutErrorResponseSchema = z.object({
    success: z.literal(false),
    error: z.object({
        code: z.string(),
        message: z.string(),
        details: z.any().optional(),
    }),
});

/**
 * Tax breakdown item for order preview
 */
export const TaxBreakdownItemSchema = z.object({
    taxRateId: z.number().int().positive(),
    name: z.string().min(1, 'Tax rate name is required'),
    rate: z.number().min(0),
    type: z.enum(['percentage', 'fixed', 'none']),
    taxableAmount: z.number().min(0),
    taxAmount: z.number().min(0),
});

/** A cart line a promo code was kept off ("Exclude from discounts"), named back to the guest. */
export const CouponSkippedItemSchema = z.object({
    name: z.string(),
    quantity: z.number().int().min(1),
    cents: z.number().int().min(0),
});

/**
 * Order preview response
 * Returns calculated totals before final checkout
 */
export const OrderPreviewSchema = z.object({
    subtotal: z.number().min(0),
    tax: z.number().min(0),
    tip: z.number().min(0),
    deliveryFee: z.number().min(0),
    total: z.number().min(0),
    // The Online Service Fee, already INCLUDED in `total`; null when none
    // applies. This is a non-strict z.object, so an undeclared key is STRIPPED
    // on parse — before this was declared the checkout page drew no fee row
    // while the total still charged it. Every IOrderPreview key must be here
    // (`__tests__/OrderPreview.test.ts` fails otherwise).
    onlineServiceFee: z
        .object({
            amount: z.number().min(0),
            tax: z.number().min(0),
            label: z.string(),
        })
        .nullable()
        .optional(),
    taxBreakdown: z.array(TaxBreakdownItemSchema),
    // DoorDash Drive live-quote DISPLAY fields (delivery orders only; optional).
    etaMinutes: z.number().optional(),
    deliveryFeeCents: z.number().optional(),
    // True when the card will be held and captured at end of day.
    cardHold: z.boolean().optional(),
    // ASAP carts only: the kitchen's prep quote in minutes (dynamic prep time).
    prepTimeMinutes: z.number().optional(),
    // Customer Loyalty v1 (B7) — server-derived redemption applied to `total`.
    loyaltyDiscount: z
        .object({
            points: z.number().int().min(0),
            cents: z.number().int().min(0),
        })
        .optional(),
    // Coupon / promo-code — server-derived discount applied to `total` (additive
    // with loyaltyDiscount), or the rejection reason for a code that didn't apply.
    // Either may name the "Exclude from discounts" items the code skipped.
    couponDiscount: z
        .object({
            code: z.string(),
            name: z.string(),
            cents: z.number().int().min(0),
            skippedItems: z.array(CouponSkippedItemSchema).optional(),
        })
        .optional(),
    couponError: z
        .object({
            code: z.string(),
            reason: z.enum([
                'INVALID_CODE',
                'REQUIRES_APPROVAL',
                'NOT_YET_VALID',
                'EXPIRED',
                'MIN_ORDER_NOT_MET',
                'NOT_APPLICABLE',
            ]),
            minimumOrderCents: z.number().int().min(0).optional(),
            skippedItems: z.array(CouponSkippedItemSchema).optional(),
        })
        .optional(),
});

// Note: Type definitions are in @order-up/models
// These schemas validate data against those types
// Use ICheckoutCustomerInfo, ICheckoutBillingAddress, etc. from @order-up/models for type annotations

